import {
  Button,
  Calendar,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import {
  maskitoDateOptionsGenerator,
  maskitoParseDate,
  maskitoStringifyDate,
  type MaskitoDateParams,
} from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { useCallback, useEffect, useMemo, useState } from "react";

export type ValueRange = { start: string; end: string };

interface InputDateRangeProps {
  value: ValueRange;
  handleChange: (name: string, value: string | ValueRange) => void;
  min_date?: Date;
  max_date?: Date;
  disabled?: boolean;
}

type ActiveCalendar = "start" | "end" | null;

export const InputDateRange = ({
  value,
  min_date,
  max_date,
  handleChange,
  disabled,
}: InputDateRangeProps) => {
  const [open, setOpen] = useState(false);
  const [activeCalendar, setActiveCalendar] = useState<ActiveCalendar>(null);

  const [startVal, setStartVal] = useState<string | undefined>(
    value.start ?? undefined,
  );
  const [endVal, setEndVal] = useState<string | undefined>(
    value.end ?? undefined,
  );
  const [error, setError] = useState<string | undefined>(undefined);

  const params: MaskitoDateParams = { mode: "dd/mm/yyyy", separator: "/" };
  const options = maskitoDateOptionsGenerator(params);

  const startMask = useMaskito({ options });
  const endMask = useMaskito({ options });

  const onChange = (newValue: ValueRange) => {
    handleChange("date_range", newValue);
  };

  const formatFullDate = (date: Date): string => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
  };

  const parseFullDate = (val?: string): Date | undefined => {
    if (!val) return undefined;
    const [d, m, y] = val.split("/").map(Number);
    if (!d || !m || !y) return undefined;
    const parsed = new Date(y, m - 1, d);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const validateDates = useCallback(
    (start?: string, end?: string) => {
      const startText = start ?? "";
      const endText = end ?? "";

      if (!startText && !endText) {
        setError("Debe seleccionar un rango de fechas.");
        return;
      }
      if (!startText) {
        setError("La fecha de inicio es obligatoria.");
        return;
      }
      if (!endText) {
        setError("La fecha de finalización es obligatoria.");
        return;
      }

      const startDate = parseFullDate(startText);
      const endDate = parseFullDate(endText);

      if (!startDate) {
        setError("Formato de fecha de inicio incorrecto. Use dd/mm/aaaa.");
        return;
      }
      if (!endDate) {
        setError(
          "Formato de fecha de finalización incorrecto. Use dd/mm/aaaa.",
        );
        return;
      }

      if (endDate < startDate) {
        setError(
          "La fecha de finalización no puede ser menor a la fecha de inicio.",
        );
        return;
      }

      setError(undefined);
    },

    [],
  );
  useEffect(() => {
    setStartVal(value.start || "");
    setEndVal(value.end || "");
    if (value.start || value.end) validateDates(value.start, value.end);
    else setError(undefined);
  }, [value.start, value.end, validateDates]);

  const startDateObj = useMemo(() => parseFullDate(startVal), [startVal]);
  const endDateObj = useMemo(() => parseFullDate(endVal), [endVal]);

  const selectedRange = useMemo(() => {
    const from = startDateObj;
    const to = endDateObj;

    if (from && to && to < from) return { from, to: undefined };
    return { from, to };
  }, [startDateObj, endDateObj]);

  const baseDisabledRules = useMemo(() => {
    return [
      ...(min_date ? [{ before: min_date }] : []),
      ...(max_date ? [{ after: max_date }] : []),
    ];
  }, [min_date, max_date]);

  const closeCalendar = () => {
    setOpen(false);
    setActiveCalendar(null);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const openCalendar = (which: ActiveCalendar) => {
    setActiveCalendar(which);
    setOpen((prev) => (which === activeCalendar ? !prev : true));
  };

  const toMaskedOrRaw = (text: string) => {
    const parsed = maskitoParseDate(text, params);
    return parsed ? maskitoStringifyDate(parsed, params) : text;
  };

  return (
    <div className="relative">
      <GroupTextField label="" error={error}>
        <GroupTextFieldRow className="items-center">
          <div className="w-full relative text-body">
            <Input
              ref={startMask}
              className="pl-9.5 w-full"
              value={startVal ?? ""}
              placeholder="Fecha de inicio"
              group="left"
              disabled={disabled}
              onChange={(e) => {
                const text = e.target.value;
                setStartVal(text);

                const currentEnd = endVal ?? value.end ?? "";

                if (!text) {
                  onChange({ start: "", end: currentEnd });
                  validateDates("", currentEnd);
                  return;
                }

                const startFormatted = toMaskedOrRaw(text);
                onChange({ start: startFormatted, end: currentEnd });
                validateDates(startFormatted, currentEnd);
              }}
            />
            <Button
              aria-label="Abrir calendario"
              className="top-1.5 left-1 absolute text-current"
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => openCalendar("start")}
              disabled={disabled}
            >
              <Icon
                type="far"
                name="calendar"
                size="lg"
                className="text-body-quiet"
              />
            </Button>
          </div>

          <div className="w-full relative text-body">
            <Input
              ref={endMask}
              className="pl-9.5 w-full"
              value={endVal ?? ""}
              placeholder="Fecha de finalización"
              group="right"
              disabled={disabled}
              onChange={(e) => {
                const text = e.target.value;
                setEndVal(text);

                const currentStart = startVal ?? value.start ?? "";

                if (!text) {
                  onChange({ start: currentStart, end: "" });
                  validateDates(currentStart, "");
                  return;
                }

                const endFormatted = toMaskedOrRaw(text);
                onChange({ start: currentStart, end: endFormatted });
                validateDates(currentStart, endFormatted);
              }}
            />
            <Button
              aria-label="Abrir calendario"
              className="top-1.5 left-1 absolute text-current"
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => openCalendar("end")}
              disabled={disabled}
            >
              <Icon
                type="far"
                name="calendar"
                size="lg"
                className="text-body-quiet"
              />
            </Button>
          </div>
        </GroupTextFieldRow>
      </GroupTextField>

      {open && (
        <div
          className="absolute z-10 bg-white shadow rounded w-full"
          onMouseDown={(e) => e.preventDefault()}
        >
          {activeCalendar === "end" ? (
            <Calendar
              className="w-full max-w-full mx-0"
              mode="range"
              autoFocus={false}
              selected={selectedRange}
              disabled={[
                ...baseDisabledRules,
                ...(startDateObj ? [{ before: startDateObj }] : []),
              ]}
              onSelect={(range) => {
                if (!range) return;

                const currentStartText = startVal ?? value.start ?? "";
                const currentStartDate = parseFullDate(currentStartText);

                if (!currentStartDate) {
                  setError("Seleccione primero una fecha de inicio.");
                  return;
                }

                const clicked = range.to ?? range.from;
                if (!clicked) return;

                if (clicked.getTime() < currentStartDate.getTime()) {
                  setError(
                    "La fecha de finalización no puede ser menor a la fecha de inicio.",
                  );
                  return;
                }

                const newEnd = formatFullDate(clicked);

                setEndVal(newEnd);
                onChange({ start: currentStartText, end: newEnd });
                validateDates(currentStartText, newEnd);
                closeCalendar();
              }}
            />
          ) : (
            <Calendar
              className="w-full max-w-full mx-0"
              mode="range"
              autoFocus={false}
              selected={selectedRange}
              disabled={baseDisabledRules}
              onSelect={(range) => {
                if (!range) return;

                const newStart = range.from ? formatFullDate(range.from) : "";
                const newEnd = range.to ? formatFullDate(range.to) : "";

                setStartVal(newStart);
                setEndVal(newEnd);
                onChange({ start: newStart, end: newEnd });

                if (range.from && range.to) {
                  validateDates(newStart, newEnd);
                  closeCalendar();
                } else {
                  setError(undefined);
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
